/**
 * The staff console's signed-in layout (container, D39): what the signed-in route renders on the console host
 * (`config.json`'s `backend.kind: 'console'`, `services.shell === 'console'`) in place of the customer app's
 * `AppLayout`. A user of the staff tenant (`GET /me`'s `tenant.staff`) gets the console shell with its own sections
 * (`lib/console.ts`); a page that is not the console's (a rundown, a studio) goes to the console's home; anyone else is
 * told to use ACP itself (`WrongHost`) — every console route refuses them anyway.
 *
 * Phase 7f part B adds the Rollouts section's views beside this file.
 */
import { Navigate, Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { AnnouncementBanner } from '../../components/AnnouncementBanner.js';
import { ConsoleShell } from '../../components/console/ConsoleShell.js';
import { WrongHost } from '../../components/console/WrongHost.js';
import { Button } from '../../components/ui/button.js';
import { useAnnouncements, useDismissals, useLiveChanges, useMe, useServices, useSession, useTheme } from '../../app/hooks.js';
import { CONSOLE_HOME, consoleAllows, consoleNav, hostVerdict, staffOf } from '../../lib/console.js';
import { dismissalKey, undismissed } from '../../lib/dismissals.js';
import { otherTheme } from '../../lib/theme.js';

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export function ConsoleLayout() {
  const services = useServices();
  const session = useSession();
  const theme = useTheme();
  const pathname = useLocation({ select: (l) => l.pathname });
  const navigate = useNavigate();
  // The change channel (D35): the console's lists hear every tenant's changes, mirrored to the staff tenant.
  useLiveChanges();
  const me = useMe();
  const announcements = useAnnouncements();
  const dismissed = useDismissals();

  // The guard (router.tsx) keeps signed-out users off this layout; a session that ends re-runs it (start.tsx).
  if (session === null) return null;
  const signOut = () => services.sessions.signOut();

  if (me.data === undefined && me.error !== null) {
    return (
      <div className="grid min-h-screen place-content-center gap-4 p-8 text-center" role="alert" data-testid="console-me-failed">
        <p className="text-sm text-destructive">Could not read who you are: {messageOf(me.error)}</p>
        <div className="flex justify-center gap-2">
          <Button variant="outline" onClick={() => void me.refetch()}>
            Retry
          </Button>
          <Button variant="outline" onClick={signOut}>
            Sign out
          </Button>
        </div>
      </div>
    );
  }

  // A backend with no `/me` (a node) cannot be the console's: it reads as not staff.
  const staff = me.data === undefined ? null : (staffOf(me.data) ?? false);
  const verdict = hostVerdict('console', staff);
  if (verdict === 'wait') return <p className="p-8 text-center text-sm text-muted-foreground">Loading…</p>;
  if (verdict === 'use-app') return <WrongHost mode="customer-on-console" email={session.email} tenantId={session.tenantId} target={services.hosts.app} onSignOut={signOut} />;
  if (!consoleAllows(pathname)) return <Navigate to={CONSOLE_HOME} replace />;

  const shown = undismissed(announcements.data ?? [], dismissed);
  return (
    <ConsoleShell
      stage={services.backend.label}
      user={{ userId: session.userId, email: session.email, tenantId: session.tenantId }}
      nav={consoleNav(pathname)}
      onNavigate={(href) => {
        void navigate({ to: href });
      }}
      theme={theme}
      onToggleTheme={() => services.theme.set(otherTheme(theme))}
      onSignOut={signOut}
      banner={<AnnouncementBanner announcements={shown} onDismiss={(a) => services.dismissals.dismiss(dismissalKey(a))} />}
    >
      <Outlet />
    </ConsoleShell>
  );
}
